import { cn } from '@/lib/utils';
import {
  CANONICAL_STAGES,
  canonicalStageIndex,
  deriveUrgency,
  mapToCanonicalStage,
  type CanonicalStage,
  type Urgency
} from '../../activeLoanModel';
import type { ProspectLoan } from '../../types';

/**
 * Inline milestone progress bar + urgency-colored days-in-stage label.
 * Canonical six-stage model (no lead). Progress fills up to the current stage;
 * color shifts from neutral → warn → danger as daysInMilestone grows.
 */
interface MilestoneTrackProps {
  loan: Pick<ProspectLoan, 'milestone' | 'milestone_date' | 'funded_date'>;
  daysInMilestone: number | null;
  compact?: boolean;
  className?: string;
}

const URGENCY_FILL: Record<Urgency, string> = {
  ok: 'bg-primary',
  warn: 'bg-[hsl(38_92%_50%)]',
  urgent: 'bg-destructive'
};

const URGENCY_TEXT: Record<Urgency, string> = {
  ok: 'text-muted-foreground',
  warn: 'text-warning',
  urgent: 'text-destructive'
};

const STAGE_LABEL_OVERRIDES: Partial<Record<CanonicalStage, string>> = {
  clear_to_close: 'CTC'
};

export function MilestoneTrack({ loan, daysInMilestone, compact = false, className }: MilestoneTrackProps) {
  const stage = mapToCanonicalStage(loan);
  const stageIndex = canonicalStageIndex(stage);
  const total = CANONICAL_STAGES.length; // 6
  // Fill fraction: stageIndex+1 / total so e.g. `application` shows ~17%, `funded` shows 100%.
  const fillPct = Math.min(100, Math.max(0, ((stageIndex + 1) / total) * 100));
  const urgency: Urgency = stage === 'funded' ? 'ok' : deriveUrgency(daysInMilestone);

  const displayLabel =
    STAGE_LABEL_OVERRIDES[stage] ??
    // Prefer the raw Encompass milestone text when it's meaningful; fall back to canonical.
    (loan.milestone && loan.milestone.trim() ? loan.milestone : stage.replace(/_/g, ' '));

  const daysLabel =
    daysInMilestone === null
      ? null
      : daysInMilestone === 0
        ? 'Today'
        : daysInMilestone === 1
          ? '1 day'
          : `${daysInMilestone} days`;

  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)}>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="truncate font-medium text-foreground" title={displayLabel}>
          {displayLabel}
        </span>
        {daysLabel && stage !== 'funded' && (
          <span className={cn('font-mono text-[10px] shrink-0', URGENCY_TEXT[urgency])}>
            {daysLabel}
          </span>
        )}
      </div>
      <div
        className={cn(
          'relative overflow-hidden rounded-full bg-muted',
          compact ? 'h-[3px]' : 'h-1'
        )}
        role="img"
        aria-label={`Stage ${stageIndex + 1} of ${total}`}
      >
        <div
          className={cn('absolute left-0 top-0 h-full rounded-full transition-all duration-300 ease-out', URGENCY_FILL[urgency])}
          style={{ width: `${fillPct}%` }}
        />
      </div>
    </div>
  );
}
